// Persistent planning-session state ("pending request"). Survives clarification
// turns, multiple messages, refreshes and temporary AI/backend failures, so a
// multi-turn calendar plan is never lost and duplicate retries are detected.
// This is bookkeeping/state ONLY — it never touches the real calendar.

import { scopedKey, getActiveUid } from "./userScope";

const KEY = () => scopedKey("planningRequest.v1");

export const REQ_STATUS = {
  PARSING: "PARSING",
  WAITING: "WAITING_FOR_CLARIFICATION",
  READY_TO_BUILD: "READY_TO_BUILD",
  BUILDING: "BUILDING",
  READY_FOR_REVIEW: "READY_FOR_REVIEW",
  FAILED_PARTIAL: "FAILED_PARTIAL",
  APPLIED: "APPLIED",
  CANCELLED: "CANCELLED",
};

function makeId() {
  const rnd = (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  return `req_${rnd}`;
}

export function getPendingRequest() {
  try {
    const raw = localStorage.getItem(KEY());
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function write(req) {
  if (!req) localStorage.removeItem(KEY());
  else localStorage.setItem(KEY(), JSON.stringify(req));
  window.dispatchEvent(new CustomEvent("chronicles:pending-changed"));
  return req;
}

// Start a new planning request or reuse the active one (so follow-up messages
// update the SAME request instead of creating independent ones).
export function beginOrReusePendingRequest() {
  const cur = getPendingRequest();
  if (cur && cur.status !== REQ_STATUS.APPLIED && cur.status !== REQ_STATUS.CANCELLED) {
    return write({ ...cur, updatedAt: new Date().toISOString() });
  }
  const now = new Date().toISOString();
  return write({
    requestId: makeId(),
    userId: getActiveUid() || null,
    status: REQ_STATUS.PARSING,
    stage: "intent",
    packageId: null,
    packageSig: null,
    counts: null,
    lastError: null,
    createdAt: now,
    updatedAt: now,
  });
}

export function updatePendingRequest(patch) {
  const cur = getPendingRequest() || beginOrReusePendingRequest();
  return write({ ...cur, ...patch, updatedAt: new Date().toISOString() });
}

// A stable signature of a proposal package's actionable content, used to detect
// identical retries (duplicate protection) — same request + same content = reuse.
export function packageSignature(pkg) {
  if (!pkg || !Array.isArray(pkg.items)) return "";
  const parts = pkg.items
    .filter((it) => it.status !== "REJECTED")
    .map((it) => `${it.kind}|${it.event_id || ""}|${it.new ? `${it.new.title || ""}@${it.new.date || ""} ${it.new.startHour}:${it.new.startMinute}-${it.new.endHour}:${it.new.endMinute}` : ""}`);
  return parts.sort().join("||");
}

export function markReadyForReview(pkg) {
  return updatePendingRequest({
    status: REQ_STATUS.READY_FOR_REVIEW,
    stage: "review",
    packageId: pkg.packageId,
    packageSig: packageSignature(pkg),
    counts: pkg.summary,
    lastError: null,
  });
}

export function markFailedPartial(reason) {
  const cur = getPendingRequest() || beginOrReusePendingRequest();
  return write({ ...cur, status: REQ_STATUS.FAILED_PARTIAL, lastError: reason || "generation_failed", updatedAt: new Date().toISOString() });
}

export function markApplied() {
  const cur = getPendingRequest();
  if (cur) write({ ...cur, status: REQ_STATUS.APPLIED, updatedAt: new Date().toISOString() });
  // Applied requests are cleared so a fresh conversation starts clean.
  return write(null);
}

export function clearPendingRequest() {
  return write(null);
}
